import React, { useState, useEffect } from 'react';
import { refundService } from '../services/api.service';
import {
  RotateCcw,
  Search,
  Filter,
  RefreshCw,
  Eye,
  CheckCircle2,
  Clock,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  AlertCircle,
  ShieldCheck
} from 'lucide-react';

const ITEMS_PER_PAGE = 10;

const AdminRefunds = () => {
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRefund, setSelectedRefund] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [notesInput, setNotesInput] = useState('');

  const fetchRefunds = async () => {
    try {
      setLoading(true);
      const res = await refundService.getRefunds(statusFilter);
      setRefunds(res.refunds || []);
    } catch (err) {
      console.warn("Failed to load admin refunds:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRefunds();
  }, [statusFilter]);

  const handleUpdateRefundStatus = async (refundId, nextStatus) => {
    try {
      setUpdating(true);
      const res = await refundService.updateRefundStatus(refundId, nextStatus, notesInput || undefined);
      if (res && res.refund) {
        setRefunds(prev => prev.map(r => r.id === refundId ? { ...r, refund_status: nextStatus } : r));
        setSelectedRefund(prev => prev && prev.id === refundId ? { ...prev, refund_status: nextStatus } : prev);
        fetchRefunds();
      }
    } catch (err) {
      alert("Failed to update refund status.");
    } finally {
      setUpdating(false);
      setNotesInput('');
    }
  };

  // Metrics
  const totalCount = refunds.length;
  const totalAmount = refunds.reduce((sum, r) => sum + Number(r.refund_amount || r.total_amount || 0), 0);
  const completedCount = refunds.filter(r => r.refund_status === 'Refund Completed').length;
  const pendingCount = totalCount - completedCount;

  // Search Filter
  const filteredRefunds = refunds.filter(r => {
    const term = search.toLowerCase();
    const orderNum = (r.order_number || '').toLowerCase();
    const customer = (r.user_name || r.user_email || '').toLowerCase();
    const reason = (r.reason || '').toLowerCase();
    return orderNum.includes(term) || customer.includes(term) || reason.includes(term);
  });

  const totalPages = Math.ceil(filteredRefunds.length / ITEMS_PER_PAGE) || 1;
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentRefunds = filteredRefunds.slice(indexOfFirstItem, indexOfLastItem);

  const renderStatusPill = (status) => {
    const s = status ? status.toLowerCase() : '';
    let bg = '#fff7ed';
    let color = '#c2410c';
    let border = '#ffedd5';

    if (s.includes('completed')) {
      bg = '#dcfce7';
      color = '#15803d';
      border = '#bbf7d0';
    } else if (s.includes('initiated')) {
      bg = '#eff6ff';
      color = '#1d4ed8';
      border = '#bfdbfe';
    } else if (s.includes('requested')) {
      bg = '#fff7ed';
      color = '#c2410c';
      border = '#ffedd5';
    }

    return (
      <span style={{ fontSize: '11.5px', fontWeight: 800, color, background: bg, border: `1px solid ${border}`, padding: '4px 10px', borderRadius: '6px' }}>
        {status || 'Cancellation Requested'}
      </span>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Top Banner & Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0' }}>
            Cancelled Orders & Refund Management ({totalCount})
          </h2>
          <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
            Dedicated dashboard for reviewing pre-delivery order cancellations and processing refunds.
          </p>
        </div>

        <button onClick={fetchRefunds} className="btn-secondary" style={{ width: 'auto', padding: '0 16px', height: '40px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <RefreshCw size={15} />
          <span>Refresh Data</span>
        </button>
      </div>

      {/* METRICS CARDS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
        
        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>Total Refund Value</div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#dc2626' }}>£{totalAmount.toFixed(2)}</div>
          <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Across {totalCount} cancelled orders</div>
        </div>

        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>Pending Action</div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#c2410c' }}>{pendingCount}</div>
          <div style={{ fontSize: '12px', color: '#c2410c', fontWeight: 700, marginTop: '4px' }}>Requires refund processing</div>
        </div>

        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', marginBottom: '8px' }}>Refunds Completed</div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#16a34a' }}>{completedCount}</div>
          <div style={{ fontSize: '12px', color: '#16a34a', fontWeight: 700, marginTop: '4px' }}>Money successfully refunded</div>
        </div>

      </div>

      {/* SEARCH & FILTER BAR */}
      <div className="table-card-box" style={{ padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
          
          <div className="header-search-wrapper" style={{ width: '360px', display: 'block' }}>
            <input
              type="text"
              className="header-search-input"
              placeholder="Search by order #, customer name, email or reason..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            />
            <Search className="header-search-icon" size={16} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Filter size={14} color="#64748b" />
            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 700 }}>Refund Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 12px', fontSize: '13px', fontWeight: 700, outline: 'none' }}
            >
              <option value="ALL">All Cancelled Orders</option>
              <option value="Cancellation Requested">Cancellation Requested</option>
              <option value="Refund Requested">Refund Requested</option>
              <option value="Refund Initiated">Refund Initiated</option>
              <option value="Refund Completed">Refund Completed</option>
            </select>
          </div>

        </div>

        {/* REFUNDS TABLE */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b', textAlign: 'left' }}>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>ORDER #</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>CUSTOMER</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>CANCELLATION REASON</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>REFUND AMOUNT</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>REFUND STAGE</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>DATE</th>
                <th style={{ padding: '12px 16px', fontWeight: 800, textAlign: 'right' }}>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>Loading cancelled orders...</td></tr>
              ) : currentRefunds.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>No cancelled orders found.</td></tr>
              ) : (
                currentRefunds.map((rf) => (
                  <tr key={rf.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '14px 16px', fontWeight: 900, color: '#0f172a' }}>
                      {rf.order_number || `#ORD-${rf.order_id}`}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: 800, color: '#0f172a' }}>{rf.user_name || 'Customer'}</div>
                      <div style={{ fontSize: '11.5px', color: '#64748b' }}>{rf.user_email || 'N/A'}</div>
                    </td>
                    <td style={{ padding: '14px 16px', color: '#475569', maxWidth: '240px' }}>
                      {rf.reason || 'Customer cancelled before delivery.'}
                    </td>
                    <td style={{ padding: '14px 16px', fontWeight: 900, color: '#dc2626' }}>
                      £{Number(rf.refund_amount || rf.total_amount || 0).toFixed(2)}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      {renderStatusPill(rf.refund_status)}
                    </td>
                    <td style={{ padding: '14px 16px', color: '#64748b', fontSize: '12px' }}>
                      {new Date(rf.created_at).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                      <button
                        onClick={() => setSelectedRefund(rf)}
                        style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                      >
                        <Eye size={14} />
                        <span>Manage Refund</span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
              Page {currentPage} of {totalPages}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#ffffff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600 }}
              >
                <ChevronLeft size={16} />
                <span>Previous</span>
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#ffffff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600 }}
              >
                <span>Next</span>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

      </div>

      {/* MANAGE REFUND MODAL */}
      {selectedRefund && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#ffffff', borderRadius: '24px', width: '100%', maxWidth: '640px', padding: '32px', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a', margin: '0 0 2px 0' }}>
                  Manage Refund - {selectedRefund.order_number || `#ORD-${selectedRefund.order_id}`}
                </h3>
                <span style={{ fontSize: '12.5px', color: '#64748b' }}>
                  Customer: {selectedRefund.user_name} ({selectedRefund.user_email})
                </span>
              </div>
              <button onClick={() => setSelectedRefund(null)} style={{ background: 'none', border: 'none', fontSize: '18px', fontWeight: 800, cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>

            {/* Refund Overview */}
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '16px', padding: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 700 }}>Refund Amount</div>
                <div style={{ fontSize: '22px', fontWeight: 900, color: '#dc2626' }}>£{Number(selectedRefund.refund_amount || selectedRefund.total_amount || 0).toFixed(2)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 700 }}>Current Stage</div>
                {renderStatusPill(selectedRefund.refund_status)}
              </div>
            </div>

            {/* Step-by-Step Strict Sequential Refund Stage Control */}
            <div>
              <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Advance Refund Lifecycle Stage
              </div>

              {(() => {
                const currentSt = selectedRefund.refund_status || 'Refund Requested';
                const nextStepMap = {
                  'Cancellation Requested': { nextStage: 'Refund Initiated', label: '1-Click Action: Approve & Initiate Refund (Refund Initiated)' },
                  'Cancellation Recorded': { nextStage: 'Refund Initiated', label: '1-Click Action: Approve & Initiate Refund (Refund Initiated)' },
                  'Refund Requested': { nextStage: 'Refund Initiated', label: '1-Click Action: Approve & Initiate Refund (Refund Initiated)' },
                  'Refund Initiated': { nextStage: 'Refund Completed', label: '1-Click Action: Mark Payment Settled (Refund Completed)' }
                };

                const nextAction = nextStepMap[currentSt];

                if (!nextAction) {
                  return (
                    <div style={{ background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '14px 18px', color: '#15803d', fontSize: '13px', fontWeight: 800, textAlign: 'center' }}>
                      ✔ Refund has been fully completed and settled. No further actions possible.
                    </div>
                  );
                }

                return (
                  <button
                    disabled={updating}
                    onClick={() => handleUpdateRefundStatus(selectedRefund.id, nextAction.nextStage)}
                    style={{
                      width: '100%',
                      padding: '14px',
                      borderRadius: '12px',
                      fontSize: '14px',
                      fontWeight: 900,
                      cursor: updating ? 'not-allowed' : 'pointer',
                      border: 'none',
                      background: '#dc2626',
                      color: '#ffffff',
                      boxShadow: '0 4px 12px rgba(220, 38, 38, 0.25)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px'
                    }}
                  >
                    <span>{updating ? 'Updating...' : nextAction.label}</span>
                    <ArrowRight size={16} />
                  </button>
                );
              })()}
            </div>

            {/* Optional Audit Note */}
            <div>
              <label style={{ fontSize: '12.5px', fontWeight: 800, color: '#0f172a', display: 'block', marginBottom: '6px' }}>Audit Note / Reference ID (Optional)</label>
              <input
                type="text"
                placeholder="e.g. Stripe Refund Ref #re_3N..."
                value={notesInput}
                onChange={(e) => setNotesInput(e.target.value)}
                style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', fontSize: '13px', outline: 'none' }}
              />
            </div>

            {/* Refund History Timeline Array */}
            {Array.isArray(selectedRefund.history) && selectedRefund.history.length > 0 && (
              <div>
                <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a', marginBottom: '10px', textTransform: 'uppercase' }}>Refund Audit Trail</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', background: '#fafbfc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '14px', maxHeight: '160px', overflowY: 'auto' }}>
                  {selectedRefund.history.map((h, hIdx) => (
                    <div key={hIdx} style={{ fontSize: '12px', display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: '6px' }}>
                      <span style={{ fontWeight: 800, color: '#0f172a' }}>{h.status}</span>
                      <span style={{ color: '#64748b' }}>{new Date(h.created_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      )}

    </div>
  );
};

export default AdminRefunds;
